<script setup>
import { onMounted, ref, onBeforeUnmount, nextTick, computed } from "vue";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";

defineOptions({
  name: "HousingTierTooltip",
});

const { housingData, tooltipClass } = defineProps({
  housingData: {
    type: Object,
    default: null,
  },
  tooltipClass: {
    type: String,
    default: "bg-white", // the tooltip container background
  },
});
const tippyInstance = ref(null);
const tooltipReferenceElement = ref(null);

const getComputedStyles = (selector) => {
  const element = document.querySelector(selector);
  if (element) {
    const computedStyles = window.getComputedStyle(element);
    return {
      background: computedStyles.backgroundColor,
      color: computedStyles.color,
    };
  }
  return {
    background: "inherit",
    color: "inherit",
  };
};

const initTippy = () => {
  if (tippyInstance.value) {
    // Destroy any existing instances to avoid multiple tooltips
    tippyInstance.value.forEach((instance) => instance.destroy());
  }

  const styles = getComputedStyles(`#tier-tooltip-${housingData.id}`);
  const template = document.getElementById(`tier-tooltip-${housingData.id}`);

  tippyInstance.value = tippy(tooltipReferenceElement.value, {
    content: template.innerHTML,
    allowHTML: true,
    role: "tooltip",
    placement: "top",
    theme: "custom-1",
    maxWidth: 500,
    aria: {
      content: "describedby",
      expanded: "auto",
    },
    onShow(instance) {
      // Apply the styles dynamically to the tooltip
      if (!_.isEmpty(styles)) {
        instance.popper.querySelector(".tippy-box").style.backgroundColor =
          styles.background;
        instance.popper.querySelector(".tippy-box").style.color = styles.color;
        instance.popper.querySelector(".tippy-arrow").style.color =
          styles.background;
      }
    },
  });
};
const showTooltip = computed(() => {
  return housingData?.tier?.id !== null && housingData?.tier?.id !== 1;
});

onMounted(() => {
  // Ensure the DOM is fully updated before initializing Tippy
  nextTick(() => {
    if (showTooltip.value) {
      initTippy();
    }
  });
});

onBeforeUnmount(() => {
  if (tippyInstance.value) {
    tippyInstance.value.destroy();
  }
});
</script>

<template>
  <div
    v-if="showTooltip"
    ref="tooltipReferenceElement"
    data-testid="housing-tier-tooltip"
    class="ml-2"
  >
    <svg
      width="21"
      height="21"
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20.2464 10.0469C20.2464 15.1534 16.0939 19.2969 10.9669 19.2969C5.83997 19.2969 1.6875 15.1534 1.6875 10.0469C1.6875 4.94032 5.83997 0.796875 10.9669 0.796875C16.0939 0.796875 20.2464 4.94032 20.2464 10.0469Z"
        :stroke="`${secondaryColor}` ? `${secondaryColor}` :`#007F80`"
        stroke-width="1.5"
      />
      <path
        d="M10.9909 6.33887C10.3469 6.33887 9.87094 5.87687 9.87094 5.28887C9.87094 4.70087 10.3469 4.23887 10.9909 4.23887C11.6349 4.23887 12.1109 4.67287 12.1109 5.24687C12.1109 5.86287 11.6489 6.33887 10.9909 6.33887ZM10.1089 15.0469V7.57087H11.8589V15.0469H10.1089Z"
        :fill="`${secondaryColor}` ? `${secondaryColor}` :`#007F80`"
      />
    </svg>
  </div>

  <!-- Tooltip content -->
  <div
    v-if="showTooltip"
    :id="`tier-tooltip-${housingData.id}`"
    class="hidden font-montserrat text-indigo-base"
    :class="tooltipClass"
  >
    <div class="flex font-montserrat">
      <div class="px-2">
        <svg
          width="21"
          height="21"
          viewBox="0 0 21 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.2464 10.0469C20.2464 15.1534 16.0939 19.2969 10.9669 19.2969C5.83997 19.2969 1.6875 15.1534 1.6875 10.0469C1.6875 4.94032 5.83997 0.796875 10.9669 0.796875C16.0939 0.796875 20.2464 4.94032 20.2464 10.0469Z"
            :stroke="`${secondaryColor}` ? `${secondaryColor}` :`#007F80`"
            stroke-width="1.5"
          />
          <path
            d="M10.9909 6.33887C10.3469 6.33887 9.87094 5.87687 9.87094 5.28887C9.87094 4.70087 10.3469 4.23887 10.9909 4.23887C11.6349 4.23887 12.1109 4.67287 12.1109 5.24687C12.1109 5.86287 11.6489 6.33887 10.9909 6.33887ZM10.1089 15.0469V7.57087H11.8589V15.0469H10.1089Z"
            :fill="`${secondaryColor}` ? `${secondaryColor}` :`#007F80`"
          />
        </svg>
      </div>
      <p v-if="housingData?.tier?.id === 2" data-testid="classic-tier">
        <strong>CLASSIC</strong> - included in the program cost, this housing
        includes all the essentials.
      </p>
      <p v-else-if="housingData?.tier?.id === 3" data-testid="comfort-tier">
        <strong>COMFORT</strong> - offered for an additional cost, this housing
        includes additional, high-end amenities.
      </p>
    </div>
  </div>
</template>

<style>
.tippy-box[data-theme~="custom-1"] {
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.15);
}
</style>
